import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api, { financeAPI, notificationsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Import Modular Financial Components
import StatsHeader from '../components/financial-hub/StatsHeader';
import BatchBreakdownTable from '../components/financial-hub/BatchBreakdownTable';
import BatchDrilldownModal from '../components/financial-hub/BatchDrilldownModal';
import { useStudentAlert } from '../hooks/useStudentAlert';
import { useTranslation } from 'react-i18next';
import StudentAlertModal from '../components/common/StudentAlertModal';

export default function FinancialHub() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Interactive details cockpit
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // 📊 Month-by-month fee history states
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const [feeHistory, setFeeHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [toast, setToast] = useState(null);

  const alertContext = useStudentAlert();

  const statsIdRef = React.useRef(0);
  const batchStudentsIdRef = React.useRef(0);
  const historyIdRef = React.useRef(0);
  const commsIdRef = React.useRef(0);

  const fetchStats = useCallback((isMounted = true) => {
    if (!token) return;
    const currentId = ++statsIdRef.current;
    setLoading(true);
    api.get('/admin/financial-hub')
      .then(res => { if (isMounted && currentId === statsIdRef.current) { setData(res.data); setLoading(false); } })
      .catch(err => { 
         if (isMounted && currentId === statsIdRef.current) {
           console.error('Financial Hub Sync Error:', err); 
           setLoading(false); 
         }
      });
  }, [token]);

  useEffect(() => {
    let isMounted = true;
    fetchStats(isMounted);
    return () => { isMounted = false; };
  }, [fetchStats]);

  // Load students for a selected batch
  useEffect(() => {
    let isMounted = true;
    if (!selectedBatch) return;
    const currentId = ++batchStudentsIdRef.current;
    setLoadingStudents(true);
    setExpandedStudentId(null); // Clear active expanded row
    financeAPI.getStudentBalances({ batchId: selectedBatch.id || selectedBatch._id, limit: 100 })
      .then(res => {
        if (isMounted && currentId === batchStudentsIdRef.current) {
          setBatchStudents(res.data.students || []);
          setLoadingStudents(false);
        }
      })
      .catch(err => {
        if (isMounted && currentId === batchStudentsIdRef.current) {
          console.error('Failed to load batch students:', err);
          setLoadingStudents(false);
          showToast('Failed to load batch students.', 'error');
        }
      });
    return () => { isMounted = false; };
  }, [selectedBatch]);

  // Reset all student sub-pane states when batch modal is closed or changed
  useEffect(() => {
    if (!selectedBatch) {
      setExpandedStudentId(null);
      setFeeHistory(null);
    }
  }, [selectedBatch]);

  // Fetch month-by-month payment history when student is expanded inline
  const [communicationHistory, setCommunicationHistory] = useState(null);
  const [loadingCommunication, setLoadingCommunication] = useState(false);

  // Named function so it can be called after sending an alert too
  const refreshCommunicationHistory = useCallback((studentId, isMounted = true) => {
    if (!studentId) return;
    const currentId = ++commsIdRef.current;
    setLoadingCommunication(true);
    notificationsAPI.getHistory(studentId)
      .then(res => {
        if (isMounted && currentId === commsIdRef.current) {
          setCommunicationHistory(res.data.notifications || []);
          setLoadingCommunication(false);
        }
      })
      .catch(err => {
        if (isMounted && currentId === commsIdRef.current) {
          console.error('Failed to load communication history:', err);
          setLoadingCommunication(false);
          setCommunicationHistory([]);
        }
      });
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!expandedStudentId) {
      setFeeHistory(null);
      setCommunicationHistory(null);
      return;
    }
    
    // Fetch Fee History
    const currentId = ++historyIdRef.current;
    setLoadingHistory(true);
    const batchIdParam = selectedBatch ? (selectedBatch.id || selectedBatch._id) : undefined;
    financeAPI.getStudentFeeHistory(expandedStudentId, batchIdParam)
      .then(res => {
        if (isMounted && currentId === historyIdRef.current) {
          setFeeHistory(res.data.history || []);
          setLoadingHistory(false);
        }
      })
      .catch(err => {
        if (isMounted && currentId === historyIdRef.current) {
          console.error('Failed to load fee history:', err);
          setLoadingHistory(false);
          setFeeHistory([]);
        }
      });

    // Fetch Communication History
    refreshCommunicationHistory(expandedStudentId, isMounted);
    return () => { isMounted = false; };
  }, [expandedStudentId, selectedBatch, refreshCommunicationHistory]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  if (loading) return <Layout title={t('financialHub.title')}><div className="dash-loading">⏳ {t('financialHub.loading')}</div></Layout>;
  if (!data) return <Layout title={t('financialHub.title')}><div className="dash-error">{t('financialHub.error')}</div></Layout>;

  return (
    <Layout title={t('financialHub.title')}>
      {/* 🎨 Premium Micro-Animations style block */}
      <style>{`
        .table-row-hover {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .table-row-hover:hover {
          background-color: rgba(43, 13, 21, 0.035) !important;
          transform: scale(1.004) translateX(4px);
          box-shadow: inset 4px 0 0 var(--wine), 0 6px 20px rgba(0, 0, 0, 0.04) !important;
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* 🚀 Toast Notifications */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24,
          background: toast.type === 'error' ? '#c0392b' : '#2d7a52',
          color: '#fff', padding: '16px 28px', borderRadius: 12,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)', zIndex: 1100,
          fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10,
          animation: 'fadeIn 0.2s ease'
        }}>
          <span>{toast.type === 'error' ? '⚠️' : '✅'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* 🚀 Stats Summary Block */}
      <StatsHeader data={data} />

      {/* 📊 Detailed Analysis: Batch Breakdown Table */}
      <BatchBreakdownTable 
        batchBreakdown={data.batchBreakdown} 
        onSelectBatch={setSelectedBatch} 
      />

      <div style={{ marginTop: 24, padding: 20, background: 'var(--wine-pale)', borderRadius: 12, border: '1px solid var(--wine-dim)', display: 'flex', alignItems: 'center', gap: 12 }}>
         <span style={{ fontSize: 20 }}>💡</span>
         <div style={{ fontSize: 13, color: 'var(--wine)' }}>
            <strong>{t('financialHub.tipTitle')}</strong>{t('financialHub.tipText')}</div>
      </div>

      {/* 🚀 DRILL DOWN INTERACTIVE MODAL */}
      <BatchDrilldownModal
        selectedBatch={selectedBatch}
        onClose={() => setSelectedBatch(null)}
        batchStudents={batchStudents}
        loadingStudents={loadingStudents}
        expandedStudentId={expandedStudentId}
        setExpandedStudentId={setExpandedStudentId}
        feeHistory={feeHistory}
        loadingHistory={loadingHistory}
        communicationHistory={communicationHistory}
        loadingCommunication={loadingCommunication}
        alertContext={alertContext}
      />

      <StudentAlertModal 
        alertContext={{
          ...alertContext,
          sendAlert: (e) => alertContext.sendAlert(e, () => refreshCommunicationHistory(expandedStudentId))
        }} 
      />

    </Layout>
  );
}
